import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Button, Col, Divider, Flex, Input, InputNumber, Modal, Row, Select, message } from "antd"
import { CloseOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { getAddOnCategories, createAddOnCategory } from "store/trips"
import ImageCropper from "ui/ImageCropper"
import styles from "./CreateAddOnModal.module.css"
import { spacings } from "themes/variables"
import { MAX_MEDIA_SIZE } from "constants/form"
import { useTranslation } from "react-i18next"

const { TextArea } = Input

export const CreateAddOnModal = ({ open, onCancel, onSave, isSaving, initialValues }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const addOnCategories = useSelector((state) => state.trips.addOnCategories.data)
  const isCategoriesLoading = useSelector((state) => state.trips.addOnCategories.loading)
  const [newCategoryName, setNewCategoryName] = useState("")
  const newCategoryInputRef = useRef(null)

  const categoryOptions = addOnCategories.map((c) => ({ value: c.id, label: c.name }))

  const handleAddCategory = async () => {
    const name = newCategoryName.trim()
    if (!name) return
    await dispatch(createAddOnCategory(name))
    setNewCategoryName("")
    setTimeout(() => newCategoryInputRef.current?.focus(), 0)
  }

  useEffect(() => {
    dispatch(getAddOnCategories())
  }, [dispatch])

  const [photo, setPhoto] = useState(null)
  const [photoFile, setPhotoFile] = useState(null)
  const [cropSrc, setCropSrc] = useState(null)
  const [isCropVisible, setIsCropVisible] = useState(false)
  const [pendingFile, setPendingFile] = useState(null)
  const [category, setCategory] = useState(null)
  const [title, setTitle] = useState("")
  const [price, setPrice] = useState(null)
  const [minPerOrder, setMinPerOrder] = useState(null)
  const [maxPerOrder, setMaxPerOrder] = useState(null)
  const [description, setDescription] = useState("")
  const inputRef = useRef(null)

  useEffect(() => {
    if (open && initialValues) {
      setPhoto(initialValues.photo ?? null)
      setPhotoFile(null) // never carry over a prior File; user must re-upload to change photo
      setCategory(initialValues.category ?? null)
      setTitle(initialValues.title ?? "")
      setPrice(initialValues.price ?? null)
      setMinPerOrder(initialValues.minPerOrder ?? null)
      setMaxPerOrder(initialValues.maxPerOrder ?? null)
      setDescription(initialValues.description ?? "")
    }
  }, [open, initialValues])

  const maxPerOrderError =
    maxPerOrder != null && minPerOrder != null && maxPerOrder < minPerOrder
      ? "Max/per Order must be greater than or equal to Min/per Order"
      : null

  const canSave = !!category && title.trim() !== "" && price != null && description.trim() !== "" && !maxPerOrderError

  const closeCropper = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc)
    setCropSrc(null)
    setPendingFile(null)
    setIsCropVisible(false)
  }

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    if (file.size / 1024 / 1024 > MAX_MEDIA_SIZE.image) {
      message.error(t("media_upload.messages.image_size_limit_exceeded", { maxSize: MAX_MEDIA_SIZE.image }))
      return
    }
    const objectUrl = URL.createObjectURL(file)
    setCropSrc(objectUrl)
    setPendingFile(file)
    setIsCropVisible(true)
  }

  const handleCropComplete = (croppedBlob) => {
    if (!croppedBlob || !pendingFile) { closeCropper(); return }
    const base = (pendingFile.name || "image").replace(/\.[^/.]+$/, "")
    const croppedFile = new File([croppedBlob], `${base}-cropped.jpg`, { type: "image/jpeg" })
    const previewUrl = URL.createObjectURL(croppedBlob)
    setPhoto(previewUrl)
    setPhotoFile(croppedFile)
    closeCropper()
  }

  const handleRemovePhoto = (e) => {
    e.stopPropagation()
    setPhoto(null)
    setPhotoFile(null)
  }

  const handleSave = async () => {
    if (!canSave) return
    await onSave?.({ photo, photoFile, category, title, price, minPerOrder, maxPerOrder, description })
    handleCancel()
  }

  const handleCancel = () => {
    closeCropper()
    setPhoto(null)
    setPhotoFile(null)
    setCategory(null)
    setTitle("")
    setPrice(null)
    setMinPerOrder(null)
    setMaxPerOrder(null)
    setDescription("")
    onCancel?.()
  }

  return (
    <Modal open={open} onCancel={handleCancel} title={"Create Add-Ons"} footer={null} width={560} centered>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhotoChange} />

      {/* Photo upload */}
      <div className="mb-m">
        {photo ? (
          <div className={styles.photoPreview}>
            <img src={photo} alt="addon" className={styles.photoImg} />
            <button className={styles.removeBtn} onClick={handleRemovePhoto} type="button">
              <CloseOutlined />
            </button>
          </div>
        ) : (
          <button type="button" className={styles.uploadCard} onClick={() => inputRef.current?.click()}>
            <UploadOutlined className={styles.uploadIcon} />
            <span className={styles.uploadLabel}>{"Upload"}</span>
            <span className={styles.uploadHint}>{"Maximum file size 10MB"}</span>
          </button>
        )}
      </div>

      {/* Category */}
      <div className="mb-m">
        <label className={styles.label}>{"Category *"}</label>
        <Select
          value={category}
          onChange={setCategory}
          placeholder="Select"
          options={categoryOptions}
          loading={isCategoriesLoading}
          showSearch
          filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          size="large"
          style={{ width: "100%" }}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <Flex gap={8} className="px-s pb-s">
                <Input
                  ref={newCategoryInputRef}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddCategory() } }}
                  placeholder="New category name"
                  size="small"
                />
                <Button type="text" icon={<PlusOutlined />} onClick={handleAddCategory} disabled={!newCategoryName.trim()}>
                  {"Add"}
                </Button>
              </Flex>
            </>
          )}
        />
      </div>

      {/* Title */}
      <div className="mb-m">
        <label className={styles.label}>{"Title *"}</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" size="large" />
      </div>

      {/* Price / Min / Max */}
      <Row gutter={16} className="mb-m">
        <Col span={8}>
          <label className={styles.label}>{"Price *"}</label>
          <InputNumber value={price} onChange={setPrice} prefix="$" placeholder="00.00" min={0} size="large" style={{ width: "100%" }} />
        </Col>
        <Col span={8}>
          <label className={styles.label}>{"Min/per Order"}</label>
          <InputNumber value={minPerOrder} onChange={setMinPerOrder} placeholder="e.g. 1" min={0} size="large" style={{ width: "100%" }} />
        </Col>
        <Col span={8}>
          <label className={styles.label}>{"Max/per Order"}</label>
          <InputNumber
            value={maxPerOrder}
            onChange={setMaxPerOrder}
            placeholder="e.g. 10"
            min={0}
            size="large"
            style={{ width: "100%" }}
            status={maxPerOrderError ? "error" : undefined}
          />
          {maxPerOrderError && <div className={styles.fieldError}>{maxPerOrderError}</div>}
        </Col>
      </Row>

      {/* Description */}
      <div className="mb-xl">
        <label className={styles.label}>{"Description *"}</label>
        <TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter your description here"
          rows={4}
          size="large"
        />
      </div>

      <Flex gap={spacings.S}>
        <Button onClick={handleCancel} className={styles.actionBtn}>{"Cancel"}</Button>
        <Button onClick={handleSave} disabled={!canSave} loading={isSaving} type="primary" className={styles.actionBtn}>{"Save"}</Button>
      </Flex>

      <ImageCropper
        visible={isCropVisible}
        onClose={closeCropper}
        imageSrc={cropSrc || ""}
        onCropComplete={handleCropComplete}
        lockSize={false}
      />
    </Modal>
  )
}

CreateAddOnModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  isSaving: PropTypes.bool,
  initialValues: PropTypes.shape({
    photo: PropTypes.string,
    photoFile: PropTypes.instanceOf(File),
    category: PropTypes.string,
    title: PropTypes.string,
    price: PropTypes.number,
    minPerOrder: PropTypes.number,
    maxPerOrder: PropTypes.number,
    description: PropTypes.string,
  }),
}

CreateAddOnModal.defaultProps = {
  onSave: undefined,
  isSaving: false,
  initialValues: null,
}
